import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Wand2, Clock, Frame, Volume2, ArrowRight, Play } from "lucide-react";

const samples = [
  { url: "https://images.unsplash.com/photo-1783792894893-8332ee93d1de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", label: "Montre connectée" },
  { url: "https://images.unsplash.com/photo-1770732766528-d0e9fd0df233?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", label: "Sérum cosmétique" },
  { url: "https://images.unsplash.com/photo-1635329086694-d02f81549dea?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", label: "Écouteurs sans fil" },
];

const features = [
  { icon: Clock, title: "Clips courts prêts à poster", desc: "Formats 5s ou 10s calibrés pour le feed TikTok." },
  { icon: Frame, title: "Format 9:16 natif", desc: "Vertical plein écran, aussi dispo en 16:9 et 1:1." },
  { icon: Volume2, title: "Voix & ambiance IA", desc: "Ajoutez une bande son produit, ou coupez-la." },
  { icon: Wand2, title: "Zéro tournage", desc: "Décrivez le produit, l'IA filme à votre place." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="studio-bg grain min-h-screen relative">
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        {/* hero */}
        <section className="pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-green-300 bg-green-500/10 border border-green-500/25 rounded-full px-3 py-1.5">
              <Sparkle /> Spécial affiliés TikTok Shop
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6 leading-[1.05]">
              Générez vos vidéos IA pour
              <span className="bg-gradient-to-r from-[#3ECF8E] via-emerald-300 to-lime-200 bg-clip-text text-transparent"> TikTok Shop</span> en quelques secondes
            </h1>
            <p className="text-base sm:text-lg text-slate-400 mt-6 max-w-xl leading-relaxed">
              Transformez n'importe quel produit en vidéo publicitaire percutante — sans caméra, sans acteur, sans montage.
              Décrivez votre produit, Viralyne crée le clip vertical prêt à poster et à booster vos commissions d'affilié.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Button
                onClick={() => navigate(user ? "/studio" : "/login")}
                className="h-12 px-7 text-base bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold"
                data-testid="hero-cta-button"
              >
                Créer ma vidéo TikTok Shop <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/pricing")}
                className="h-12 px-7 text-base rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white"
                data-testid="hero-pricing-button"
              >
                Voir les tarifs
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-4 font-mono">Abonnements dès 19,99 €/mois · crédits renouvelés chaque mois · annulable à tout moment.</p>
          </div>

          <div className="grid grid-cols-2 gap-4 fade-up" style={{ animationDelay: "0.15s" }}>
            {samples.map((s, i) => (
              <div
                key={i}
                className={`relative overflow-hidden rounded-2xl border border-white/10 group ${i === 0 ? "col-span-2 aspect-video" : "aspect-square"}`}
              >
                <img src={s.url} alt={s.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/15 backdrop-blur grid place-items-center">
                    <Play className="w-4 h-4 text-white fill-white" />
                  </div>
                  <span className="text-xs text-white/90 font-medium">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* features */}
        <section className="pb-28">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <div key={i} className="glass rounded-2xl p-6 hover:border-green-500/40 transition-colors fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="w-11 h-11 rounded-xl bg-green-500/15 border border-green-500/25 grid place-items-center mb-4">
                  <f.icon className="w-5 h-5 text-green-300" />
                </div>
                <h3 className="font-display text-lg font-semibold">{f.title}</h3>
                <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-display font-extrabold">Vira<span className="text-[#3ECF8E]">lyne</span></span>
          <p className="text-xs text-slate-500">Vidéos IA pour affiliés TikTok Shop · © {new Date().getFullYear()} Viralyne</p>
        </div>
      </footer>
    </div>
  );
}

function Sparkle() {
  return <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />;
}
